type GreetingCardSessionSource =
  | 'shopify_iframe'
  | 'yuzu_iframe'
  | 'yuzu_session_page'

type GreetingCardSessionEventInput = {
  sessionId: string
  shop: string
  source: GreetingCardSessionSource
  templateId?: string
}

type GreetingCardSessionSavedEventInput = GreetingCardSessionEventInput & {
  operation: 'created' | 'updated'
}

type PostHogEnvironment = 'dev' | 'prod'

export function createGreetingCardSessionOpenedEvent(
  input: GreetingCardSessionEventInput,
  environment: PostHogEnvironment,
) {
  return {
    event: 'greeting_card_session_opened',
    properties: {
      $process_person_profile: false,
      environment,
      greeting_card_session_id: input.sessionId,
      service: 'greeting-card-builder',
      shop: input.shop,
      source: input.source,
      template_id: input.templateId,
    },
  }
}

export function createGreetingCardSessionSavedEvent(
  input: GreetingCardSessionSavedEventInput,
  environment: PostHogEnvironment,
) {
  return {
    event: 'greeting_card_session_saved',
    properties: {
      $process_person_profile: false,
      environment,
      greeting_card_session_id: input.sessionId,
      operation: input.operation,
      service: 'greeting-card-builder',
      shop: input.shop,
      source: input.source,
      template_id: input.templateId,
    },
  }
}

/** Captures only identifiers needed to find a GCB recording; never card content. */
export function useGreetingCardSessionTracking() {
  const { $clientPosthog } = useNuxtApp()
  const configuredEnvironment = useRuntimeConfig().public.posthog.environment
  const environment =
    configuredEnvironment === 'dev' || configuredEnvironment === 'prod'
      ? configuredEnvironment
      : undefined

  if (!environment) {
    return {
      captureOpened: (_input: GreetingCardSessionEventInput) => undefined,
      captureSaved: (_input: GreetingCardSessionSavedEventInput) => undefined,
    }
  }

  return {
    captureOpened(input: GreetingCardSessionEventInput) {
      const event = createGreetingCardSessionOpenedEvent(input, environment)
      $clientPosthog?.capture(event.event, event.properties)
    },
    captureSaved(input: GreetingCardSessionSavedEventInput) {
      const event = createGreetingCardSessionSavedEvent(input, environment)
      $clientPosthog?.capture(event.event, event.properties)
    },
  }
}
